import {toast} from '@wandb/components';
import type {
  TurnResponse,
  WbAgentClient,
} from '@wandb/weave/lib/WbAgent/WbAgentClient';

import {
  beginRename,
  endRename,
  renameThread as renameThreadAction,
} from '../store/actions';
import type {AgentChatDispatch, AgentChatStore} from '../store/context';

export type RenameThreadParams = {
  client: WbAgentClient;
  dispatch: AgentChatDispatch;
  store: AgentChatStore;
  threadId: string;
  title: string;
  abortControllers?: Map<string, AbortController>;
};

/**
 * defaultAbortControllers holds the pending PATCH controller for each
 * thread. Tests pass their own map for isolation.
 */
export const defaultAbortControllers = new Map<string, AbortController>();

/**
 * renameThread performs an optimistic title update. A second call for
 * the same thread aborts and supersedes the first.
 */
export async function renameThread({
  client,
  dispatch,
  store,
  threadId,
  title,
  abortControllers = defaultAbortControllers,
}: RenameThreadParams): Promise<void> {
  title = title.trim();
  const priorThread = store.getState().threads[threadId];
  if (!priorThread) {
    return;
  }

  // Compare against the stored title only — saving while viewing a
  // derived fallback intentionally locks it in as a real persisted title.
  if (title === (priorThread.title?.trim() ?? '')) {
    return;
  }

  abortControllers.get(threadId)?.abort();
  const controller = new AbortController();
  abortControllers.set(threadId, controller);

  dispatch(beginRename(threadId, title));

  try {
    const response = await client.patch<TurnResponse>(
      `/turns/${threadId}`,
      {title},
      undefined,
      controller.signal
    );

    const titleUpdated = response.title?.trim() || null;
    const serverUpdatedAt = Date.parse(response.updated_at);
    dispatch(
      renameThreadAction(
        threadId,
        titleUpdated,
        serverUpdatedAt,
        serverUpdatedAt
      )
    );
    dispatch(endRename(threadId));
  } catch (err) {
    if (controller.signal.aborted || (err as Error).name === 'AbortError') {
      return;
    }

    dispatch(endRename(threadId));
    // Suppress the toast if the user deleted the thread mid-rename; the failure
    // isn't actionable for a thread that's already discarded.
    if (store.getState().threads[threadId]) {
      toast.error('Failed to rename chat.');
    }
  } finally {
    if (abortControllers.get(threadId) === controller) {
      abortControllers.delete(threadId);
    }
  }
}
